import React from "react";
import { ArrowRight, ChevronRight, ShieldCheck, QrCode, CheckCircle2 } from "lucide-react";

const STAGES = ["Detect", "Erase", "Verify", "Certify", "Label", "Power off"];

const EraseHero = () => {
  return (
    <section id="top" className="relative overflow-hidden bg-brand-bg font-body">
      {/* atmosphere */}
      <div className="bg-dot-grid pointer-events-none absolute inset-0 opacity-60" aria-hidden />
      <div
        className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-brand-orchid/25 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-24 top-40 h-[26rem] w-[26rem] rounded-full bg-brand-magenta/15 blur-3xl"
        aria-hidden
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:gap-8 lg:py-28">
        {/* ── Left: copy ── */}
        <div>
          <span
            className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-line bg-white/80 px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum shadow-sm backdrop-blur"
            style={{ animationDelay: "0ms" }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-magenta" />
            Furloop Poofed · Verified Data Erasure
          </span>

          <h1
            className="animate-fade-up mt-6 font-display text-4xl font-semibold leading-[1.06] tracking-tight text-brand-plum sm:text-5xl lg:text-6xl"
            style={{ animationDelay: "80ms" }}
          >
            Poof. Your data&apos;s gone —{" "}
            <span className="text-brand-gradient">and we can prove it.</span>
          </h1>

          <p
            className="animate-fade-up mt-6 max-w-xl text-base leading-relaxed text-ink sm:text-lg"
            style={{ animationDelay: "160ms" }}
          >
            Verified, provable data erasure for every refurbished device — automatic, hands-off, and
            auditable. Every drive is wiped to international data-sanitization standards
            <em> before it ever reaches a new owner</em>, then verified and certified. No leftover files,
            no recoverable data, just a clean machine and a signed certificate that proves it.
          </p>

          {/* trust line */}
          <div
            className="animate-fade-up mt-6 inline-flex flex-wrap items-center gap-2.5 rounded-full border border-line bg-white px-3.5 py-2 shadow-sm"
            style={{ animationDelay: "220ms" }}
          >
            <ShieldCheck className="h-5 w-5 text-brand-magenta" />
            <span className="text-sm font-medium text-brand-plum">NIST SP 800-88 Rev. 2</span>
            <span className="hidden text-sm text-muted sm:inline">· IEEE 2883-2022 · verified read-back</span>
          </div>

          {/* CTAs */}
          <div
            className="animate-fade-up mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
            style={{ animationDelay: "280ms" }}
          >
            <a
              href="#pilot"
              className="group inline-flex items-center gap-2 rounded-full bg-cta-gradient px-6 py-3 text-sm font-semibold text-white no-underline shadow-lg shadow-[#304BEF]/25 transition-transform duration-200 hover:-translate-y-0.5 hover:no-underline"
            >
              Join the Pilot Program
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </a>
            <a
              href="#workflow"
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-5 py-3 text-sm font-semibold text-brand-plum no-underline transition-colors hover:border-brand-orchid hover:text-brand-magenta hover:no-underline"
            >
              See how it works
              <ChevronRight className="h-4 w-4" />
            </a>
          </div>

          {/* stage pipeline */}
          <div className="animate-fade-up mt-10" style={{ animationDelay: "340ms" }}>
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
              {STAGES.map((step, i) => (
                <React.Fragment key={step}>
                  <span className="rounded-full border border-line bg-white/70 px-3 py-1 text-xs font-medium text-brand-plum">
                    {step}
                  </span>
                  {i < STAGES.length - 1 && (
                    <ChevronRight className="h-3.5 w-3.5 text-brand-orchid" />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="mt-3 max-w-lg text-xs leading-relaxed text-muted">
              From a single power-on, every drive is detected, erased with the right method, verified,
              certified, labelled, and powered off — dozens in parallel, with zero babysitting.
            </p>
          </div>
        </div>

        {/* ── Right: signed certificate showpiece ── */}
        <div className="animate-fade-up relative" style={{ animationDelay: "200ms" }}>
          <div className="relative rounded-3xl border border-line bg-white/50 p-5 shadow-xl shadow-brand-plum/5 backdrop-blur-sm sm:p-8">
            <CertificateCard />
          </div>
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-line bg-white px-4 py-2 text-center text-xs font-medium text-brand-plum shadow-sm">
            One device · one certificate · zero doubt
          </div>
        </div>
      </div>
    </section>
  );
};

const ROWS = [
  ["Device", "Dell Latitude 5420"],
  ["Drive", "NVMe SSD · 512 GB"],
  ["Method", "Firmware sanitize (block erase)"],
  ["Standard", "NIST SP 800-88 Rev. 2"],
];

const CertificateCard = () => (
  <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-sm">
    {/* header */}
    <div className="flex items-center justify-between border-b border-line bg-brand-bg px-5 py-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-brand-magenta" />
        <span className="font-display text-sm font-semibold text-brand-plum">Erasure Certificate</span>
      </div>
      <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-semibold text-success">
        <CheckCircle2 className="h-3.5 w-3.5" /> Verified
      </span>
    </div>

    {/* body */}
    <div className="px-5 py-4">
      <dl className="space-y-2.5 text-sm">
        {ROWS.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4">
            <dt className="shrink-0 text-muted">{k}</dt>
            <dd className="text-right font-medium text-brand-plum">{v}</dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-4">
          <dt className="shrink-0 text-muted">Verification</dt>
          <dd className="inline-flex items-center gap-1 font-semibold text-success">
            <CheckCircle2 className="h-4 w-4" /> Read-back PASS
          </dd>
        </div>
      </dl>

      {/* scannable proof */}
      <div className="mt-5 flex items-center gap-4 rounded-xl border border-line bg-brand-bg/70 p-4">
        <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-line bg-white text-brand-plum">
          <QrCode className="h-9 w-9" strokeWidth={1.2} />
        </span>
        <div>
          <div className="text-xs font-semibold text-brand-plum">Scan to verify independently</div>
          <p className="mt-1 text-[11px] leading-relaxed text-muted">
            Tamper-evident, digitally signed. The buyer can confirm this device was erased to standard.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default EraseHero;
